import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from '@/components/ui';
import {
  VENDOR_DOC_TYPE_LABELS,
  VendorDocType,
  vendorService,
} from '../../../services/vendorService';

interface Props {
  show: boolean;
  onHide: () => void;
  vendorId: number;
  onSaved: () => Promise<void> | void;
}

/**
 * Liability insurance drives both the expiry indicator and the activation check off its date,
 * so it must carry one. Everything else may be undated: quotes and general documents
 * (signed questionnaires, agreements) usually have no expiry, and a WCB clearance is often
 * issued without one - demanding a made-up date is worse than recording none.
 */
/**
 * V2-058: PDF, Word and images only. Mirrors COMPLIANCE_FILE_EXTENSIONS in
 * server/src/modules/vendor/vendor-document.files.ts; the picker lists only these.
 */
const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'doc', 'jpg', 'jpeg', 'png', 'heic'];
export const DOCUMENT_ACCEPT = DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(',');
export const DOCUMENT_FILE_ERROR = 'Upload a PDF, Word document or image (JPG, PNG, HEIC).';

export function isAllowedDocumentFile(file: Pick<File, 'name'>): boolean {
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  return DOCUMENT_EXTENSIONS.includes(ext);
}

const EXPIRY_REQUIRED: VendorDocType[] = ['LIABILITY_INSURANCE'];

/**
 * Attaching a document is an occasional act, so it lives behind a button rather than sitting
 * permanently open under the list (which read as though the page were always mid-upload).
 */
const VendorDocumentModal: React.FC<Props> = ({ show, onHide, vendorId, onSaved }) => {
  const [docType, setDocType] = useState<VendorDocType>('WCB');
  const [file, setFile] = useState<File | null>(null);
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setDocType('WCB');
    setFile(null);
    setIssuedAt('');
    setExpiresAt('');
    setError(null);
  }, [show]);

  const expiryRequired = EXPIRY_REQUIRED.includes(docType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Choose a file to upload.');
      return;
    }
    if (!isAllowedDocumentFile(file)) {
      setError(DOCUMENT_FILE_ERROR);
      return;
    }
    if (expiryRequired && !expiresAt) {
      // Mirrors the server rule, with the reason: a WCB row with no expiry silently disables
      // both the warning triangle and the activation block for this vendor.
      setError(
        `An expiry date is required for ${VENDOR_DOC_TYPE_LABELS[docType]} – the expiry warning and the activation check both read it.`
      );
      return;
    }

    try {
      setUploading(true);
      setError(null);
      await vendorService.addDocument(vendorId, {
        docType,
        file,
        issuedAt: issuedAt || undefined,
        expiresAt: expiresAt || undefined,
      });
      await onSaved();
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Attach Document</Modal.Title>
      </Modal.Header>
      {/* The form sits between the panel and Modal.Body, so it must carry the flex-column
          contract itself. Without it Body grows to full content height and the footer is
          clipped by the panel's overflow. */}
      <Form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group controlId="vendor-doc-type" className="tw-mb-3">
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value as VendorDocType)}
                >
                  {Object.entries(VENDOR_DOC_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="vendor-doc-file" className="tw-mb-3">
                <Form.Label>File *</Form.Label>
                <Form.Control
                  type="file"
                  // M15: PDF or image only - the server refuses anything else.
                  accept={DOCUMENT_ACCEPT}
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    setFile(input.files?.[0] ?? null);
                  }}
                />
                <Form.Text className="tw-text-content-muted">PDF, Word or image (JPG, PNG, HEIC), up to 25 MB.</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="vendor-doc-issued" className="tw-mb-3">
                <Form.Label>Issued</Form.Label>
                <Form.Control
                  type="date"
                  value={issuedAt}
                  onChange={(e) => setIssuedAt(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="vendor-doc-expires" className="tw-mb-3">
                <Form.Label>Expires {expiryRequired && '*'}</Form.Label>
                <Form.Control
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  required={expiryRequired}
                />
                <Form.Text>
                  {expiryRequired
                    ? 'Required – drives the expiry warning and the activation check.'
                    : 'Optional – leave blank for a document that does not expire.'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={onHide} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default VendorDocumentModal;
